import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Alert,
  Paper,
  styled
} from '@mui/material';
import {
  CloudUpload,
  PhotoCamera,
  Delete,
  CheckCircle
} from '@mui/icons-material';
import { shades } from '../../../theme';

const Input = styled('input')({
  display: 'none'
});

const ProfilePicture = ({ onValidityChange, onDataChange, initialData = {} }) => {
  // Consolidated form data management
  const [formData, setFormData] = useState({
    image: '',
    previewUrl: ''
  });
  const [error, setError] = useState(null);

  // On mount, check for initialData or stored data
  useEffect(() => {
    setFormData((prevData) => {
      if (!prevData.previewUrl) {
        if (initialData?.previewUrl) {
          onValidityChange(true);
          return {
            image: initialData.image || '',
            previewUrl: initialData.previewUrl
          };
        }

        // Otherwise, check localStorage
        const storedPreview = localStorage.getItem('profilePreviewUrl');
        if (storedPreview) {
          onValidityChange(true);
          const updatedData = {
            image: prevData.image || '',
            previewUrl: storedPreview
          };

          // Notify parent component
          onDataChange?.(updatedData);

          return updatedData;
        }
      }

      return prevData; // Prevents unnecessary state updates
    });
  }, []); // Empty dependency array ensures it runs only once

  // File validation
  const validateFile = (file) => {
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif'];
    const maxSize = 5 * 1024 * 1024; // 5MB

    if (!allowedTypes.includes(file.type)) {
      return 'Please upload a valid image file (JPG, PNG, or GIF)';
    }
    if (file.size > maxSize) {
      return 'File size should be less than 5MB';
    }
    return null;
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      const validationError = validateFile(file);
      if (validationError) {
        setError(validationError);
        onValidityChange(false);
        return;
      }

      const reader = new FileReader();
      reader.onload = () => {
        const updatedData = {
          image: file,
          previewUrl: reader.result
        };

        setFormData(updatedData);
        setError(null);
        onValidityChange(true); // Enable next button
        localStorage.setItem('profilePreviewUrl', reader.result);

        // Notify parent component
        if (onDataChange) {
          onDataChange(updatedData);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemove = () => {
    const updatedData = {
      image: null,
      previewUrl: null
    };

    setFormData(updatedData);
    setError(null);
    onValidityChange(false); // Disable next button
    localStorage.removeItem('profilePreviewUrl');

    // Notify parent component
    if (onDataChange) {
      onDataChange(updatedData);
    }
  };

  return (
    <Box sx={{ width: '100%', maxWidth: 600, mx: 'auto', p: 3 }}>
      {/* Format Guidelines */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 3,
          bgcolor: 'rgba(0, 115, 255, 0.05)',
          border: '1px solid rgba(0, 115, 255, 0.1)',
          borderRadius: 2
        }}
      >
        <Typography variant='subtitle2' color='primary' gutterBottom>
          📸 Image Upload Guidelines:
        </Typography>
        <Typography variant='body2' color={shades.neutral[600]}>
          • Accepted formats: JPG, PNG, GIF
          <br />
          • Maximum file size: 5MB
          <br />
          • Recommended: A clear headshot with good lighting
        </Typography>
      </Paper>

      {/* Error Display */}
      {error && (
        <Alert
          severity='error'
          sx={{ mb: 3 }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      {/* Upload Area */}
      {!formData.previewUrl ? (
        <Box
          sx={{
            border: `2px dashed ${shades.neutral[300]}`,
            borderRadius: 2,
            p: 4,
            textAlign: 'center',
            bgcolor: 'white',
            transition: 'all 0.3s ease',
            '&:hover': {
              borderColor: 'rgb(0, 115, 255)',
              bgcolor: 'rgba(0, 115, 255, 0.02)'
            }
          }}
        >
          <PhotoCamera sx={{ fontSize: 48, color: shades.neutral[400], mb: 2 }} />
          <Typography variant='h6' color={shades.neutral[700]} gutterBottom>
            Upload Your Profile Picture
          </Typography>

          <label htmlFor='image-upload'>
            <Input
              accept='image/jpeg,image/png,image/gif'
              id='image-upload'
              type='file'
              onChange={handleFileChange}
            />
            <Button
              variant='contained'
              component='span'
              startIcon={<CloudUpload />}
              sx={{
                mt: 2,
                bgcolor: 'rgb(0, 115, 255)',
                '&:hover': {
                  bgcolor: 'rgb(0, 90, 200)'
                }
              }}
            >
              Choose Image
            </Button>
          </label>
        </Box>
      ) : (
        <Box sx={{ textAlign: 'center' }}>
          {/* Preview Area */}
          <Box sx={{ position: 'relative', display: 'inline-block' }}>
            <Box
              component='img'
              src={formData.previewUrl}
              alt='Profile preview'
              sx={{
                width: 200,
                height: 200,
                borderRadius: '50%',
                objectFit: 'cover',
                border: '4px solid white',
                boxShadow: '0 0 0 2px rgb(0, 115, 255, 0.2)'
              }}
            />

            {/* Success Indicator */}
            <CheckCircle
              sx={{
                position: 'absolute',
                bottom: 0,
                right: 0,
                color: 'rgb(0, 115, 255)',
                bgcolor: 'white',
                borderRadius: '50%',
                fontSize: 28
              }}
            />
          </Box>

          {/* Action Buttons */}
          <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'center' }}>
            <Button
              variant='outlined'
              startIcon={<Delete />}
              onClick={handleRemove}
              sx={{
                borderColor: 'rgb(211, 47, 47)',
                color: 'rgb(211, 47, 47)',
                '&:hover': {
                  borderColor: 'rgb(211, 47, 47)',
                  bgcolor: 'rgba(211, 47, 47, 0.04)'
                }
              }}
            >
              Remove
            </Button>

            <label htmlFor='image-change'>
              <Input
                accept='image/jpeg,image/png,image/gif'
                id='image-change'
                type='file'
                onChange={handleFileChange}
              />
              <Button
                variant='outlined'
                component='span'
                startIcon={<PhotoCamera />}
                sx={{
                  borderColor: 'rgb(0, 115, 255)',
                  color: 'rgb(0, 115, 255)',
                  '&:hover': {
                    borderColor: 'rgb(0, 115, 255)',
                    bgcolor: 'rgba(0, 115, 255, 0.04)'
                  }
                }}
              >
                Change Photo
              </Button>
            </label>
          </Box>

          {/* Success Message */}
          <Typography
            variant='body2'
            sx={{
              color: 'rgb(0, 115, 255)',
              mt: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1
            }}
          >
            <CheckCircle sx={{ fontSize: 16 }} />
            Image uploaded successfully! Click 'Next' to continue
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default ProfilePicture;
